<template>
  <FileUpload
    v-model:files="form.files"
    :buildingId="item.tenant.id"
    multiple
    :disabled="busy > 0"
  />
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { computed, inject, ref, watch } from "vue";
import { useForm } from "@inertiajs/vue3";
import { FileUpload } from "@/SharedComponents/Inputs";
import { toast } from "@/SharedComponents/Toaster";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["back", "update:item"]);

const props = defineProps({
  item: { type: Object, required: true },
  building: { type: Object, required: true },
});

const form = useForm({
  files: [],
});

function cancel() {
  form.reset();
  emit("back");
}

const busy = ref(0);
const uploadedFiles = ref([]);

const { t } = useI18n();
const { hasPermission, permissions } = inject("permissions");

function submit() {
  if (busy.value > 0) return;

  if (!hasPermission(permissions.createDocument, props.building.id)) {
    toast.error(t("toasts.no_permission.files.create"));
    return;
  }

  busy.value = form.files.length;
  form.files
    .filter((file) => file.uploadProgress === 0)
    .forEach((file) => {
      axios
        .post(
          route("files.store"),
          {
            ...file.data(),
            tenant_id: props.item.tenant.id,
            space_ids: [props.item.id],
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (event) => {
              file.uploadProgress = event.progress;
              file.remove = false;
              file.loading = true;
            },
          }
        )
        .then((res) => {
          uploadedFiles.value.push(res.data);
        })
        .catch((err) => {
          file.clearErrors();
          if (err.response?.status === 422) {
            Object.keys(err.response.data.errors).forEach((key) => {
              file.setError(key, err.response.data.errors[key][0]);
            });
          }
        })
        .finally(() => {
          file.uploadProgress = 1.1;
          busy.value--;
        });
    });
}

watch(busy, (value) => {
  if (!value) {
    emit("update:item", {
      item: {
        ...props.item,
        files: [...props.item.files, ...uploadedFiles.value],
      },
      callback: () => {
        uploadedFiles.value = [];
      },
    });
    cancel();
  }
});

defineExpose({
  isDirty: computed(() => form.isDirty),
  cancel,
  submit,
});
</script>
